import { useEffect, useRef, useState } from "react";
import { getTTSUrl } from "@/lib/tts";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, RotateCw, Loader2 } from "lucide-react";

export default function TTSAudioPlayer({ text, testIdPrefix = "tts-audio" }) {
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [rate, setRate] = useState("1");

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    getTTSUrl(text, user?.settings).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = parseFloat(rate);
  }, [rate, url]);

  const skip = (delta) => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + delta);
  };

  if (!url) {
    return (
      <div className="flex items-center justify-center py-3" data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 w-full" data-testid={testIdPrefix}>
      {/* eslint-disable-next-line */}
      <audio ref={audioRef} src={url} controls className="w-full" data-testid={`${testIdPrefix}-element`} />
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="icon" onClick={() => skip(-5)} data-testid={`${testIdPrefix}-back-button`}>
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Select value={rate} onValueChange={setRate}>
          <SelectTrigger className="w-20 h-9" data-testid={`${testIdPrefix}-speed-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {["0.75", "1", "1.25", "1.5"].map((r) => (
              <SelectItem key={r} value={r} data-testid={`${testIdPrefix}-speed-${r}`}>{r}x</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="icon" onClick={() => skip(5)} data-testid={`${testIdPrefix}-forward-button`}>
          <RotateCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
